/**
 * Knowledgebase file viewer: a side panel on desktop, full screen on a phone.
 * Opened by `?doc=<fileId>` on the Knowledgebase Hub (one URL per document).
 *
 * - Preview tab: Google's embedded preview (drivePreviewUrl). It renders only for
 *   someone signed in to Google with access to the shared drive; when it can't
 *   (signed out, third-party cookies blocked) the frame shows Google's own error,
 *   which the page can't detect cross-origin, so a short note is always shown.
 * - Text tab: the extracted text Omega Wolf already stores (GET /kb/documents/{id}),
 *   rendered without inserting any HTML (KbTextView).
 * - Open in Drive, Copy link (the Omega Wolf deep link), Close (Esc too).
 */
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faLink, faXmark } from '@fortawesome/free-solid-svg-icons';
import { DWA } from '../theme/dwaTokens';
import { drivePreviewUrl, fetchKbDocument, type KbDocument } from '../services/kbApi';
import { KbTextView } from './KbTextView';

export const KB_PREVIEW_NOTE = 'Sign in to Google with your DWA account to see the preview, or use the Text tab.';

type Tab = 'preview' | 'text';

const button: React.CSSProperties = {
  padding: '0.45rem 0.85rem',
  borderRadius: DWA.radius,
  fontSize: '0.8rem',
  fontWeight: 700,
  fontFamily: DWA.font,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

function kbDocLink(fileId: string): string {
  return `${window.location.origin}/hubs/knowledgebase?doc=${encodeURIComponent(fileId)}`;
}

export const KbViewer: React.FC<{ fileId: string; onClose: () => void }> = ({ fileId, onClose }) => {
  const [doc, setDoc] = useState<KbDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('preview');
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setDoc(null);
    setError(null);
    setTab('preview');
    fetchKbDocument(fileId)
      .then((d) => live && setDoc(d))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [fileId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copy = () => {
    const link = kbDocLink(fileId);
    const done = () => setCopied('Link copied');
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(link).then(done, () => setCopied(link));
      } else {
        setCopied(link);
      }
    } catch {
      setCopied(link);
    }
  };

  const tabButton = (id: Tab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === id}
      onClick={() => setTab(id)}
      style={{
        ...button,
        border: 'none',
        borderBottom: `3px solid ${tab === id ? DWA.gold : 'transparent'}`,
        borderRadius: 0,
        background: 'none',
        color: tab === id ? DWA.ink : DWA.textSecondary,
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="kb-viewer-root" style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div aria-hidden="true" onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={doc?.title ?? 'Document'}
        data-testid="kb-viewer"
        style={{
          position: 'relative',
          width: 'min(760px, 100vw)',
          height: '100%',
          background: '#ffffff',
          color: DWA.ink,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-12px 0 40px rgba(0,0,0,0.35)',
          fontFamily: DWA.font,
        }}
      >
        <header style={{ padding: '0.9rem 1rem 0', borderBottom: `1px solid ${DWA.cardBorder}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem', overflowWrap: 'anywhere' }}>{doc?.title ?? (error ? 'Document' : 'Loading…')}</h2>
              {doc && (
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: DWA.textSecondary, overflowWrap: 'anywhere' }}>
                  {[doc.kind_label, doc.folder_path, doc.modified_time && `Updated ${formatDate(doc.modified_time)}${doc.last_modified_by ? ` by ${doc.last_modified_by}` : ''}`]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
            </div>
            <button type="button" onClick={onClose} aria-label="Close viewer" style={{ ...button, border: 'none', background: 'none', color: DWA.textSecondary, fontSize: '1.1rem' }}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', margin: '0.7rem 0' }}>
            {doc?.web_view_link && (
              <a href={doc.web_view_link} target="_blank" rel="noopener noreferrer" className="hub-gold-button" style={{ ...button, background: DWA.gold, color: DWA.onGold, border: 'none' }}>
                Open in Drive <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '0.7rem' }} />
              </a>
            )}
            <button type="button" onClick={copy} style={{ ...button, background: 'transparent', color: DWA.ink, border: `1px solid ${DWA.cardBorder}` }}>
              <FontAwesomeIcon icon={faLink} /> Copy link
            </button>
            {copied && (
              <span role="status" style={{ fontSize: '0.75rem', color: DWA.textSecondary, alignSelf: 'center', overflowWrap: 'anywhere' }}>
                {copied}
              </span>
            )}
          </div>
          <div role="tablist" style={{ display: 'flex', gap: '0.25rem' }}>
            {tabButton('preview', 'Preview')}
            {tabButton('text', 'Text')}
          </div>
        </header>

        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {error && (
            <p role="alert" style={{ margin: '1rem', color: DWA.danger, fontSize: '0.88rem' }}>
              Couldn't load this document: {error}
            </p>
          )}
          {tab === 'preview' && !error && (
            <>
              <p data-testid="kb-preview-note" style={{ margin: 0, padding: '0.45rem 1rem', fontSize: '0.75rem', color: DWA.textSecondary, background: '#f8f8f6' }}>
                {KB_PREVIEW_NOTE}
              </p>
              <iframe
                key={fileId}
                title={`Preview of ${doc?.title ?? 'document'}`}
                src={drivePreviewUrl(fileId, doc?.mime_type)}
                // Cross-origin (Google), so already isolated from this page; no sandbox,
                // which could stop Google's own viewer from working.
                referrerPolicy="no-referrer"
                style={{ flex: 1, width: '100%', border: 'none', minHeight: 0 }}
              />
            </>
          )}
          {tab === 'text' && !error && (
            <div data-testid="kb-text-tab" style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
              {!doc && <p style={{ color: DWA.textSecondary }}>Loading…</p>}
              {doc && !doc.text && (
                <p style={{ color: DWA.textSecondary, fontSize: '0.88rem' }}>
                  No text was extracted from this file ({doc.extract_status === 'metadata_only' ? "its type isn't read inside" : 'extraction failed'}).
                  Use the preview or open it in Drive.
                </p>
              )}
              {doc?.text && <KbTextView text={doc.text} format={doc.text_format} />}
              {doc?.text_truncated && (
                <p style={{ color: DWA.textSecondary, fontSize: '0.78rem', fontStyle: 'italic' }}>Text shortened: open in Drive for the full document.</p>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};
